/**
 * ProxyHealthCard Component
 *
 * Displays detailed proxy health information with individual proxy status
 * and manual health check capabilities
 */

import { useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaSync,
  FaServer,
} from "react-icons/fa";
import { Button } from "./Button";
import { checkProxyHealth } from "../../utils/scraperHealthCheck";
import "./ProxyHealthCard.scss";

export const ProxyHealthCard = ({ scraper, onRefresh }) => {
  const [checkingProxy, setCheckingProxy] = useState(null);
  const [proxyResults, setProxyResults] = useState({});

  const handleCheckProxy = async (proxyUrl) => {
    if (!scraper?.url) return;

    setCheckingProxy(proxyUrl);

    try {
      const result = await checkProxyHealth(
        scraper.url,
        proxyUrl,
        scraper.apiKey,
      );

      setProxyResults((prev) => ({
        ...prev,
        [proxyUrl]: result,
      }));

      // Clear result after 10 seconds
      setTimeout(() => {
        setProxyResults((prev) => {
          const updated = { ...prev };
          delete updated[proxyUrl];
          return updated;
        });
      }, 10000);
    } catch (error) {
      console.error("Failed to check proxy:", error);
    } finally {
      setCheckingProxy(null);
    }
  };

  if (!scraper?.proxyStats) {
    return (
      <div className="info-card">
        <h3>Proxy Information</h3>
        <div className="info-content">
          <div className="empty-message">
            <FaServer className="empty-icon" />
            <span>No proxy information available</span>
            <p className="empty-hint">
              Run a health check to fetch proxy details
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { proxyStats } = scraper;
  const hasDetails = proxyStats.details && proxyStats.details.length > 0;

  return (
    <div className="info-card proxy-health-card">
      <div className="card-header">
        <h3>Proxy Information</h3>
        {onRefresh && (
          <Button variant="link" size="small" icon={FaSync} onClick={onRefresh}>
            Refresh
          </Button>
        )}
      </div>

      <div className="info-content">
        {/* Summary Stats */}
        <div className="proxy-stats-summary">
          <div className="stat-item">
            <span className="stat-label">Total Proxies</span>
            <span className="stat-value">{proxyStats.total || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Enabled</span>
            <span className="stat-value stat-value--success">
              {proxyStats.enabled || 0}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Disabled</span>
            <span className="stat-value stat-value--muted">
              {proxyStats.disabled || 0}
            </span>
          </div>
        </div>

        {/* Detailed Proxy List */}
        {hasDetails && (
          <div className="proxy-details-list">
            <h4>Proxy Status</h4>
            {proxyStats.details.map((proxy, index) => {
              const isChecking = checkingProxy === proxy.url;
              const manualResult = proxyResults[proxy.url];
              const displayStatus = manualResult || proxy;

              return (
                <div key={index} className="proxy-item">
                  <div className="proxy-item__info">
                    <div className="proxy-item__url">
                      <FaServer className="proxy-icon" />
                      <span>{proxy.url}</span>
                    </div>

                    <div className="proxy-item__status">
                      {/* Configuration Status */}
                      {proxy.configured ? (
                        <span className="status-badge status-badge--enabled">
                          Enabled
                        </span>
                      ) : (
                        <span className="status-badge status-badge--disabled">
                          Disabled
                        </span>
                      )}

                      {/* Health Status */}
                      {proxy.configured && (
                        <>
                          {displayStatus.responding || displayStatus.healthy ? (
                            <span className="status-badge status-badge--healthy">
                              <FaCheckCircle /> Responding
                            </span>
                          ) : (
                            <span className="status-badge status-badge--unhealthy">
                              <FaExclamationTriangle />{" "}
                              {displayStatus.status || displayStatus.error}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Manual Check Button */}
                  <Button
                    variant="link"
                    size="small"
                    onClick={() => handleCheckProxy(proxy.url)}
                    disabled={isChecking}
                  >
                    {isChecking ? (
                      <>
                        <FaSpinner className="spinner" /> Checking...
                      </>
                    ) : (
                      "Check Health"
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!hasDetails && proxyStats.total > 0 && (
          <div className="proxy-hint">
            <p>Run a detailed health check to see individual proxy status</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProxyHealthCard;
